import { ReactComponent as PhoneSvg } from "../../../../common/icons/IconPhone/svg/phone.svg";
import { ReactComponent as MessageSvg } from "../../../../common/icons/IconMessage/svg/message.svg";
import { IconWhatsAppOutline } from "../../../../common/icons/IconWhatsAppOutline/IconWhatsAppOutline";
import { ReactComponent as EmailSvg } from "../../../../common/icons/IconEmail/svg/email.svg";
import defaultContent from "../../../../content/downlineProfile";
import profileContent from "../../../../content/profile";
import {
  trackCoApplicantCall,
  trackCoApplicantEmail,
  trackCoApplicantMessage,
  trackCoApplicantWhatsapp,
  trackCopyPhoneNumber,
  trackPrimaryApplicantCall,
  trackPrimaryApplicantEmail,
  trackPrimaryApplicantMessage,
  trackPrimaryApplicantWhatsapp,
  trackSponsorCall,
  trackSponsorEmail,
  trackSponsorMessage,
  trackSponsorWhatsapp,
} from "../../PlatinumProfile/downlineProfileTealium";
import { SponsorType } from "../types";
import { IconCopy } from "../../../../common/icons/IconCopy/IconCopy";
import { useTranslation } from "react-i18next";
import LeafTooltip from "../../../../common/leaf/LeafTooltip/LeafTooltip";
import { useRef } from "react";
import { TooltipRefProps } from "react-tooltip";
import MaskedPhoneNumber from "./MaskedPhoneNumber";
import { ContactLink } from "../../../../common/components/ContactLink/ContactLink";
interface ContactRowProps {
  phone?: string;
  email?: string;
  whatsApp?: string;
  isKoreaMarket?: boolean;
  iconFlags?: {
    showCallIcon?: boolean;
    showMessageIcon?: boolean;
    showEmailIcon?: boolean;
    hideWhatsApp?: boolean;
    hideDownlineContactDetails?: boolean;
  };
  tealium: {
    type: "sponsor" | "applicant";
    isPrimary?: boolean;
    sponsorType?: SponsorType;
  };
}
const ContactRow = ({ phone, email, whatsApp, isKoreaMarket = false, iconFlags, tealium }: ContactRowProps) => {
  const { t } = useTranslation(["downlineProfile", "profile"]);
  const tooltipRef = useRef<TooltipRefProps>(null);

  const handleTooltipClick = () => {
    if (tooltipRef.current) {
      // Force show tooltip on click and hide after 2 seconds
      tooltipRef.current.open();
      setTimeout(() => {
        if (tooltipRef.current) {
          tooltipRef.current.close();
        }
      }, 1000);
    }
  };
  return (
    <>
      <div className={`contact-row${isKoreaMarket ? " contact-row--korea" : ""}`}>
        {isKoreaMarket && phone?.trim() && (
          <p className="contact-row--phone">
            <MaskedPhoneNumber phone={phone} />
          </p>
        )}
        {!isKoreaMarket && iconFlags?.showCallIcon && phone?.trim() && (
          <ContactLink
            method="call"
            contact={phone}
            role="button"
            aria-label={t("phoneCall", defaultContent.phoneCall)}
            href={`tel:${phone}`}
            target="_parent"
            onClick={() => {
              if (tealium.type === "applicant" && tealium.isPrimary) {
                trackPrimaryApplicantCall();
              }
              if (tealium.type === "applicant" && !tealium.isPrimary) {
                trackCoApplicantCall();
              }
              if (tealium.type === "sponsor") {
                trackSponsorCall(tealium.sponsorType ?? "sponsor");
              }
            }}
          >
            <PhoneSvg />
          </ContactLink>
        )}
        {!isKoreaMarket && iconFlags?.showMessageIcon && phone && (
          <ContactLink
            method="text"
            contact={phone}
            role="button"
            href={`sms:${phone}`}
            aria-label={t("sms", defaultContent.sms)}
            target="_parent"
            onClick={() => {
              if (tealium.type === "applicant" && tealium.isPrimary) {
                trackPrimaryApplicantMessage();
              }
              if (tealium.type === "applicant" && !tealium.isPrimary) {
                trackCoApplicantMessage();
              }
              if (tealium.type === "sponsor") {
                trackSponsorMessage(tealium.sponsorType ?? "sponsor");
              }
            }}
          >
            <MessageSvg />
          </ContactLink>
        )}
        {!isKoreaMarket && !iconFlags?.hideWhatsApp && whatsApp && (
          <ContactLink
            method="whatsApp"
            contact={phone ?? `+${whatsApp}`}
            role="button"
            aria-label={t("whatsApp", defaultContent.whatsApp)}
            href={`https://wa.me/${whatsApp}`}
            target="_blank"
            rel="noreferrer"
            onClick={() => {
              if (tealium.type === "applicant" && tealium.isPrimary) {
                trackPrimaryApplicantWhatsapp();
              }
              if (tealium.type === "applicant" && !tealium.isPrimary) {
                trackCoApplicantWhatsapp();
              }
              if (tealium.type === "sponsor") {
                trackSponsorWhatsapp(tealium.sponsorType ?? "sponsor");
              }
            }}
          >
            <IconWhatsAppOutline />
          </ContactLink>
        )}
        {!iconFlags?.hideDownlineContactDetails && iconFlags?.showEmailIcon !== false && email && (
          <ContactLink
            method="email"
            contact={email}
            role="button"
            aria-label={t("email", defaultContent.email)}
            href={`mailto:${email}`}
            target="_blank"
            rel="noreferrer"
            onClick={() => {
              if (tealium.type === "applicant" && tealium.isPrimary) {
                trackPrimaryApplicantEmail();
              }
              if (tealium.type === "applicant" && !tealium.isPrimary) {
                trackCoApplicantEmail();
              }
              if (tealium.type === "sponsor") {
                trackSponsorEmail(tealium.sponsorType ?? "sponsor");
              }
            }}
          >
            <EmailSvg />
          </ContactLink>
        )}
        {!isKoreaMarket && iconFlags?.showCallIcon && phone?.trim() && (
          <button
            aria-label={t("copyPhone", defaultContent.copyPhone)}
            onClick={() => {
              navigator.clipboard.writeText(phone ?? "").catch(() => {});
              trackCopyPhoneNumber();
              handleTooltipClick();
            }}
          >
            <LeafTooltip
              position="left"
              id={`copy-${phone}-${Date.now()}`} //Date.now() serves as a unique hash just in case of overlapping ids (same phone numbers)
              icon={<IconCopy />}
              openOnClick={true}
              content={t("leaf.copyPhoneTooltipText", profileContent.leaf.copyPhoneTooltipText, { ns: "profile" })}
              disableDefaultHandleClick={true}
              tooltipRef={tooltipRef}
            />
          </button>
        )}
      </div>
    </>
  );
};

export default ContactRow;
